/**
 * Modal to manage kanban columns (pipelines) and their default checklists.
 *
 * Two-pane layout (matches the mockup):
 *   - LEFT: list of pipelines for the workspace. Each row is reorder-draggable,
 *     has a color swatch (popover to pick from a palette), the pipeline name
 *     (read-only — click the row to select), a checklist-count badge, and a
 *     delete button. Add-new row at the bottom.
 *   - RIGHT: when a pipeline is selected, shows an editable title input for
 *     that pipeline plus its default_checklists items — reorder-draggable,
 *     name (inline edit), required-to-advance toggle, delete. All items are
 *     checkbox-type. Add-new at the bottom.
 *
 * Writes happen IMMEDIATELY (TanStack Query handles optimistic update via
 * invalidation). No working-copy pattern — keeps state simple and matches
 * the rest of the app's mutation conventions. The dialog's "Close" button
 * just dismisses; there is no "Cancel" because there is nothing to roll back.
 */
import { useCallback, useEffect, useState } from "react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  GripVertical,
  Lock,
  LockOpen,
  Plus,
  Trash2,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter as AlertDialogFooterUI,
  AlertDialogHeader as AlertDialogHeaderUI,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { usePipeline } from "@/hooks/use-pipeline";
import {
  useDefaultChecklists,
  useDefaultChecklistCounts,
} from "@/hooks/use-default-checklists";
import { PIPELINE_COLOR_PALETTE } from "@/lib/default-pipelines";
import { isProtectedPipeline } from "@/lib/pipeline-display";
import { cn } from "@/lib/utils";
import type { DefaultChecklistRow, PipelineRow } from "@/types/pipeline";

interface ManageColumnsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/* ──────────────────────────────────────────────────────────────────────── *
 *  Sortable row primitives
 * ──────────────────────────────────────────────────────────────────────── */

interface SortableRowProps {
  id: string;
  children: React.ReactNode;
  className?: string;
}

function SortableRow({ id, children, className }: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        "flex items-center gap-2 rounded-md border border-border bg-muted/40 p-1.5",
        isDragging && "opacity-40",
        className,
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground hover:text-foreground active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden />
      </button>
      {children}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────── *
 *  Pipeline (column) list — LEFT pane
 * ──────────────────────────────────────────────────────────────────────── */

function PipelineRowItem({
  pipeline,
  selected,
  count,
  onColorChange,
  onDelete,
}: {
  pipeline: PipelineRow;
  selected: boolean;
  count: number;
  onColorChange: (id: string, color: string) => void;
  onDelete: (id: string) => void;
}) {
  const isProtected = isProtectedPipeline(pipeline);
  return (
    <SortableRow
      id={pipeline.id}
      className={cn(
        "cursor-pointer",
        selected && "border-primary bg-primary/5",
      )}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-3 w-3 flex-shrink-0 rounded-sm border border-border"
            style={{ backgroundColor: pipeline.color ?? "#8a93a3" }}
            aria-label="Change color"
            onClick={(e) => e.stopPropagation()}
          />
        </PopoverTrigger>
        <PopoverContent
          className="grid w-auto grid-cols-6 gap-1 p-1.5"
          align="start"
        >
          {PIPELINE_COLOR_PALETTE.map((c) => (
            <button
              key={c}
              type="button"
              className="h-5 w-5 rounded-sm border border-border hover:scale-110"
              style={{ backgroundColor: c }}
              onClick={() => onColorChange(pipeline.id, c)}
              aria-label={`Set color ${c}`}
            />
          ))}
        </PopoverContent>
      </Popover>

      <span className="flex-1 truncate px-1 text-xs font-semibold">
        {pipeline.title}
      </span>

      <ChevronDown
        className={cn(
          "h-3.5 w-3.5 text-muted-foreground transition",
          selected && "rotate-180 text-foreground",
        )}
        aria-hidden
      />

      {count > 0 && (
        <span className="rounded-full border border-border bg-background px-1.5 text-[10px] font-bold text-muted-foreground">
          {count}
        </span>
      )}

      {isProtected ? (
        <span
          className="grid h-6 w-6 place-items-center text-muted-foreground/60"
          aria-label="System column — cannot be deleted"
          title="System column — cannot be deleted"
        >
          <Lock className="h-3.5 w-3.5" aria-hidden />
        </span>
      ) : (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(pipeline.id);
          }}
          className="grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
          aria-label="Delete pipeline"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </SortableRow>
  );
}

/* ──────────────────────────────────────────────────────────────────────── *
 *  Checklist item row — RIGHT pane
 * ──────────────────────────────────────────────────────────────────────── */

function ChecklistRowItem({
  item,
  onTitleBlur,
  onRequiredChange,
  onDelete,
}: {
  item: DefaultChecklistRow;
  onTitleBlur: (
    id: string,
    patch: Partial<DefaultChecklistRow>,
  ) => void;
  onRequiredChange: (id: string, required: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const [text, setText] = useState(item.checklist_text);
  useEffect(() => setText(item.checklist_text), [item.checklist_text]);

  return (
    <SortableRow id={item.id}>
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          if (text.trim() && text !== item.checklist_text) {
            onTitleBlur(item.id, { checklist_text: text.trim() });
          } else if (!text.trim()) {
            setText(item.checklist_text);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setText(item.checklist_text);
            e.currentTarget.blur();
          }
        }}
        placeholder="Task name"
        className="h-7 flex-1 border-0 bg-transparent px-1 text-xs focus-visible:bg-background"
      />

      <button
        type="button"
        onClick={() => onRequiredChange(item.id, !item.required)}
        className={cn(
          "grid h-6 w-6 place-items-center rounded-full border",
          item.required
            ? "border-destructive bg-destructive/15 text-destructive"
            : "border-border bg-background text-muted-foreground hover:text-foreground",
        )}
        aria-label={
          item.required
            ? "Required (click to make optional)"
            : "Optional (click to require for advance)"
        }
        title={
          item.required
            ? "Required to advance"
            : "Mark as required to advance"
        }
      >
        {item.required ? (
          <Lock className="h-3 w-3" aria-hidden />
        ) : (
          <LockOpen className="h-3 w-3" aria-hidden />
        )}
      </button>

      <button
        type="button"
        onClick={() => onDelete(item.id)}
        className="grid h-6 w-6 place-items-center rounded text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
        aria-label="Delete checklist item"
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </SortableRow>
  );
}

/* ──────────────────────────────────────────────────────────────────────── *
 *  Main dialog
 * ──────────────────────────────────────────────────────────────────────── */

export function ManageColumnsDialog({
  open,
  onOpenChange,
}: ManageColumnsDialogProps) {
  const {
    pipelines,
    createPipeline,
    updatePipeline,
    deletePipeline,
    reorderPipelines,
  } = usePipeline();

  const { counts: checklistCounts } = useDefaultChecklistCounts();

  const [selectedPipelineId, setSelectedPipelineId] = useState<string | null>(
    null,
  );
  const [newPipelineName, setNewPipelineName] = useState("");
  const [newItemText, setNewItemText] = useState("");
  const [titleDraft, setTitleDraft] = useState("");
  const [pendingDelete, setPendingDelete] = useState<
    | { kind: "pipeline"; id: string; name: string }
    | { kind: "checklist"; id: string; name: string }
    | null
  >(null);

  // Default-select the first pipeline when the dialog opens
  useEffect(() => {
    if (open && !selectedPipelineId && pipelines.length > 0) {
      setSelectedPipelineId(pipelines[0].id);
    }
  }, [open, selectedPipelineId, pipelines]);

  const selectedPipeline =
    pipelines.find((p) => p.id === selectedPipelineId) ?? null;

  // Keep the right-pane title input in sync when selection or upstream changes.
  useEffect(() => {
    setTitleDraft(selectedPipeline?.title ?? "");
  }, [selectedPipeline?.title]);

  const commitTitle = useCallback(() => {
    if (!selectedPipeline) return;
    const next = titleDraft.trim();
    if (!next) {
      setTitleDraft(selectedPipeline.title);
      return;
    }
    if (next === selectedPipeline.title) return;
    updatePipeline.mutate({ id: selectedPipeline.id, patch: { title: next } });
  }, [selectedPipeline, titleDraft, updatePipeline]);

  const checklists = useDefaultChecklists(selectedPipelineId);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );

  /* ── Pipeline list reorder ────────────────────────────────────────── */
  const handlePipelineDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.over || event.active.id === event.over.id) return;
      const oldIndex = pipelines.findIndex((p) => p.id === event.active.id);
      const newIndex = pipelines.findIndex((p) => p.id === event.over!.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(pipelines, oldIndex, newIndex);
      reorderPipelines.mutate({ ids: reordered.map((p) => p.id) });
    },
    [pipelines, reorderPipelines],
  );

  /* ── Checklist list reorder ───────────────────────────────────────── */
  const handleChecklistDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!event.over || event.active.id === event.over.id) return;
      const items = checklists.items;
      const oldIndex = items.findIndex((i) => i.id === event.active.id);
      const newIndex = items.findIndex((i) => i.id === event.over!.id);
      if (oldIndex < 0 || newIndex < 0) return;
      const reordered = arrayMove(items, oldIndex, newIndex);
      // Persist new order one at a time (small N).
      reordered.forEach((item, i) => {
        if (item.order !== i + 1) {
          checklists.updateItem.mutate({
            id: item.id,
            patch: { order: i + 1 },
          });
        }
      });
    },
    [checklists],
  );

  function addPipeline() {
    const name = newPipelineName.trim();
    if (!name) return;
    createPipeline.mutate({ title: name }, {
      onSuccess: (row) => {
        setNewPipelineName("");
        if (row) setSelectedPipelineId(row.id);
      },
    });
  }

  function addChecklistItem() {
    if (!selectedPipelineId) return;
    const text = newItemText.trim();
    if (!text) return;
    const order =
      checklists.items.length > 0
        ? Math.max(...checklists.items.map((i) => i.order)) + 1
        : 1;
    checklists.addItem.mutate(
      {
        pipelineId: selectedPipelineId,
        checklistText: text,
        order,
      },
      { onSuccess: () => setNewItemText("") },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Manage Columns</DialogTitle>
        </DialogHeader>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
          {/* ── LEFT pane: pipeline list ────────────────────────────── */}
          <div className="flex min-h-0 flex-col gap-2 border-r border-border pr-3">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Columns · click to edit checklist
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handlePipelineDragEnd}
            >
              <SortableContext
                items={pipelines.map((p) => p.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex flex-col gap-1 overflow-y-auto">
                  {pipelines.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPipelineId(p.id)}
                      className="text-left"
                    >
                      <PipelineRowItem
                        pipeline={p}
                        selected={selectedPipelineId === p.id}
                        count={checklistCounts[p.id] ?? 0}
                        onColorChange={(id, color) =>
                          updatePipeline.mutate({ id, patch: { color } })
                        }
                        onDelete={(id) =>
                          setPendingDelete({
                            kind: "pipeline",
                            id,
                            name: p.title,
                          })
                        }
                      />
                    </button>
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <div className="mt-auto flex gap-1.5 rounded-md border border-dashed border-border bg-muted/40 p-1.5">
              <Input
                value={newPipelineName}
                onChange={(e) => setNewPipelineName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addPipeline();
                }}
                placeholder="New column name"
                className="h-7 flex-1 border-border bg-background px-2 text-xs"
              />
              <Button
                type="button"
                onClick={addPipeline}
                disabled={!newPipelineName.trim() || createPipeline.isPending}
                size="sm"
                className="h-7 px-2 text-xs"
              >
                <Plus className="h-3 w-3" aria-hidden /> Add
              </Button>
            </div>
          </div>

          {/* ── RIGHT pane: selected pipeline's checklist ──────────── */}
          <div className="flex min-h-0 flex-col gap-2">
            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Checklist
            </div>

            {selectedPipeline ? (
              <>
                <div className="flex items-center gap-2">
                  <span
                    className="h-3 w-3 rounded-sm border border-border"
                    style={{
                      backgroundColor: selectedPipeline.color ?? "#8a93a3",
                    }}
                    aria-hidden
                  />
                  <Input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") e.currentTarget.blur();
                      if (e.key === "Escape") {
                        setTitleDraft(selectedPipeline.title);
                        e.currentTarget.blur();
                      }
                    }}
                    aria-label="Pipeline name"
                    className="h-8 flex-1 border-0 bg-transparent px-1 text-base font-bold focus-visible:bg-background"
                  />
                </div>

                {checklists.items.length === 0 && !checklists.isLoading && (
                  <div className="py-10 text-center text-xs text-muted-foreground">
                    No checklist items yet
                  </div>
                )}

                {checklists.items.length > 0 && (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleChecklistDragEnd}
                  >
                    <SortableContext
                      items={checklists.items.map((i) => i.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div className="flex flex-col gap-1 overflow-y-auto">
                        {checklists.items.map((item) => (
                          <ChecklistRowItem
                            key={item.id}
                            item={item}
                            onTitleBlur={(id, patch) =>
                              checklists.updateItem.mutate({ id, patch })
                            }
                            onRequiredChange={(id, required) =>
                              checklists.updateItem.mutate({
                                id,
                                patch: { required },
                              })
                            }
                            onDelete={(id) =>
                              setPendingDelete({
                                kind: "checklist",
                                id,
                                name: item.checklist_text,
                              })
                            }
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                )}

                <div className="mt-auto flex gap-1.5 rounded-md border border-dashed border-border bg-muted/40 p-1.5">
                  <Input
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addChecklistItem();
                    }}
                    placeholder="Add a checklist item (e.g. Wire EMD)"
                    className="h-7 flex-1 border-border bg-background px-2 text-xs"
                  />
                  <Button
                    type="button"
                    onClick={addChecklistItem}
                    disabled={
                      !newItemText.trim() || checklists.addItem.isPending
                    }
                    size="sm"
                    className="h-7 px-2 text-xs"
                  >
                    <Plus className="h-3 w-3" aria-hidden /> Add
                  </Button>
                </div>
              </>
            ) : (
              <div className="py-10 text-center text-xs text-muted-foreground">
                Select a column to edit its checklist
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeaderUI>
            <AlertDialogTitle>
              {pendingDelete?.kind === "pipeline"
                ? "Delete this column?"
                : "Delete this checklist item?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.kind === "pipeline" ? (
                <>
                  &quot;{pendingDelete?.name}&quot; and every checklist
                  template under it will be removed. Deals already in this
                  column will be unassigned.
                </>
              ) : (
                <>
                  &quot;{pendingDelete?.name}&quot; will be removed from this
                  column&apos;s default checklist.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeaderUI>
          <AlertDialogFooterUI>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!pendingDelete) return;
                if (pendingDelete.kind === "pipeline") {
                  deletePipeline.mutate(pendingDelete.id);
                } else {
                  checklists.deleteItem.mutate(pendingDelete.id);
                }
                setPendingDelete(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooterUI>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
